<template>
  <div :class="className">
    <div v-if="!scheduleTemplate">
      <q-spinner-hourglass
        v-if="isLoading"
        :size="size"
        color="primary"
      />

      <q-btn
        v-else
        dense
        :flat="flat"
        :rounded="rounded"
        :outline="outline"
        unelevated
        :size="size"
        color="green-8"
        :padding="padding"
        data-testid="broker-abrir"
        @click="handleMenu"
        icon="mdi-whatsapp"
        :label="label"
        :disable="disableList"
      >
        <q-tooltip>
          {{ isLoading ? 'Atualizando Templates' : 'Enviar Mensagem' }}
        </q-tooltip>
      </q-btn>
    </div>
    <q-btn
      v-else
      color="primary"
      dense
      outline
      no-caps
      data-testid="broker-selecionar-template"
      @click="handleMenu"
      label="Selecionar Template"
      :disable="disableList"
    >
      <q-tooltip>
        {{ 'Selecionar Template' }}
      </q-tooltip>
    </q-btn>

    <q-menu
      ref="menu"
      v-model="isOpen"
      no-parent-event
      max-height="80vh"
      max-width="360px"
      content-class="min-width"
    >
      <q-item-section class="row height-30px">
        <div class="col">
          <div class="row full-height items-center search-box">
            <SearchIcon class="search-icon q-ma-sm absolute" />

            <input
              data-testid="broker-busca"
              type="text"
              class="fit input-search-field"
              v-model="filter"
              placeholder="Buscar por broker, template ..."
            >
          </div>
        </div>
      </q-item-section>
      <q-list
        dense
        bordered
        class="bg-white"
      >
        <template v-for="broker in brokers">
          <q-separator :key="`bot-${broker.botId}`" />

          <ListBrokersMenuItem
            v-if="broker.templates.length > 1 && !filterOn"
            :key="broker.id"
            :broker="broker"
            @on-handler-template-click="handlerTemplateClick"
          />

          <q-item
            dense
            clickable
            v-close-popup
            :key="broker.id"
            v-if="broker.templates.length === 1 && !filterOn "
            :data-testid="`broker-${broker.id}`"
            @click="() => handlerTemplateClick(getFirstTemplate(broker))"
          >
            <q-item-section class="label text-primary">
              <q-item-label lines="1">
                {{ broker.botName }}
              </q-item-label>
            </q-item-section>

            <q-tooltip
              anchor="top left"
              self="top right"
              :offset="[3, -5]"
              max-width="250px"
            >
              <div class="title">
                <span>{{ sanitizeName(broker.templates[0].name) }}</span>
              </div>

              <div v-html="bodyMessage(broker.templates[0])" />
            </q-tooltip>
          </q-item>

          <ListBrokersFiltered
            v-if="filterOn"
            :key="broker.id"
            :broker="broker"
            @on-handler-template-click="handlerTemplateClick"
            :menu-show="filter !==''"
          />
        </template>
      </q-list>
    </q-menu>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ListBrokersMenuItem from '@/components/ListBrokersMenuItem'
import ListBrokersFiltered from '@/components/ListBrokersFiltered'
import SearchIcon from '@/assets/icons/search-dark.svg'
import { sanitizeName } from '@/components/utils/sanitizeName'

export default {
  components: { ListBrokersMenuItem, SearchIcon, ListBrokersFiltered },

  props: {
    size: {},
    padding: { default: '0' },
    className: { default: 'col-auto q-pa-xs' },
    flat: { default: true },
    rounded: { default: true },
    outline: { default: true },
    label: { default: '' },
    disableList: { default: false },
    scheduleTemplate: { default: false }
  },

  data () {
    const isOpen = false
    const isLoaded = false
    const isLoading = false
    return { isLoading, isLoaded, isOpen, filter: '' }
  },

  computed: {
    ...mapGetters({
      templates: 'agentModule/getTemplates',
      sesssionReloading: 'agentModule/sesssionReloading'
    }),

    brokers () {
      const brokers = {}
      const filter = this.filter.trim().toLocaleLowerCase()
      for (const template of this.templates) {
        const { botId, botName, source } = template
        if (!['ZENVIA', 'WHATSAPP', 'GUPSHUP', 'WHATSAPPAPI'].includes(source)) continue
        if (!brokers[botId]) brokers[botId] = { botId, botName, source, templates: [] }

        if (filter === '') {
          brokers[botId].templates.push(template)
          continue
        }

        const brokerNameIncluded = botName.trim().toLocaleLowerCase().includes(filter)

        if (brokerNameIncluded) {
          brokers[botId].templates.push(template)
          continue
        }

        const templateNameIncluded = sanitizeName(template.name).toLocaleLowerCase().includes(filter)
        if (templateNameIncluded) brokers[botId].templates.push(template)
      }
      const filteredBrokers = Object.values(brokers).reduce(byTemplate, [])
      return filteredBrokers
    },

    filterOn () {
      return this.filter.trim() !== ''
    }
  },

  methods: {
    sanitizeName,
    bodyMessage (template) {
      try {
        const cfg = JSON.parse(template.message)
        let data = '<span>'
        if (cfg.header) data += `<strong>${cfg.header}</strong><br /><br />`
        if (cfg.data) data += `${cfg.data}`
        if (cfg.footer) data += `<br /><strong>${cfg.footer}</strong>`
        if (cfg.buttons?.length) data += `<br /><br />${cfg.buttons.map(({ text }) => `<button>${text}</button>`).join('&nbsp;')}`
        data += '</span>'
        for (const param of template.params) {
          data = data.replaceAll(param.name, `<strong>*${param.value}*</strong>`)
        }

        return data
      } catch {
        return template.message
      }
    },

    handleMenu () {
      const vm = this

      if (vm.isLoading) return

      if (vm.isLoaded) {
        vm.isOpen = true
        return
      }

      vm.isLoading = true
      this.$store.dispatch('agentModule/reloadSession')
    },

    getTemplates (sourceBroker) {
      return this.templates.filter((item) => item.source === sourceBroker)
    },

    handlerTemplateClick (template) {
      this.isOpen = false
      this.$emit('on-template-select', template)
    },

    getFirstTemplate (broker) {
      const template = { ...broker.templates[0] }
      template.broker = { ...broker }
      delete template.broker.templates
      return template
    },

    handlerBrokerClick (broker) {
      if (broker.templates.length === 1) {
        this.$emit('on-template-select', broker.templates[0])
      }
    }
  },

  watch: {
    sesssionReloading: {
      deep: true,
      handler (sesssionReloading, oldsesssionReloading) {
        const vm = this

        if (sesssionReloading === oldsesssionReloading) return
        if (sesssionReloading) return
        if (!vm.isLoading) return

        Object.assign(vm, {
          isLoading: false,
          isLoaded: true,
          isOpen: true
        })
      }
    },
    isOpen: {
      handler (isOpen) {
        if (!isOpen) this.filter = ''
      }
    }
  }
}

const byTemplate = (acc, broker) => {
  const templates = broker.templates.sort((a, b) => (a.name > b.name ? 1 : -1))
  return acc.concat({ ...broker, templates })
}
</script>

<style lang="sass" scoped>
.pt-3
  padding-top: 3px

.title
  font-weight: 600
  margin-bottom: 5px
  padding-bottom: 2px
  font-size: 11px
  border-bottom: 1px solid #999

.label
  font-size: 14px
  line-height: 16px
  font-width: normal

.input-search-field
  margin-left: 38px
  border: none
  color: #01253C
  &:focus
    border: none
    outline: none
  &:hover
    border: none
  &::-webkit-input-placeholder
    color: #01253C
    opacity: 0.5
    font-style: normal
    font-weight: normal
    font-size: 14px
    line-height: 12px
.search-box
  padding-left: 10px
.search-icon
  height: 17px
  width: 17px
.search-icon > stroke
  fill: #01253C
.height-30px
  height: 34px
</style>
